import React, { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

// Components

import SummaryItem from './SummaryItem';

// Utils

import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { dayjsUtil, toUTCAndFormat } from '@utils/dates/date.utils';

// Types

import {
    BloodRelationship,
    BloodRelationships,
    HouseholdMember,
    HouseholdMembers,
    Relations
} from '@components/ssap/SsapTypes';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';

const ApplyingForHealthCoverageSummary = props => {

    enum CellTypes {
        LABEL,
        VALUE
    }

    const {
        additionalElement,
        bloodRelationship,
        householdMembers,
        relations: {
            loadedRelations,
            loadedRelationsWholeList
        }
    } = props;

    const intlApi = useIntl();

    const areDeceasedOnApplication: boolean = useMemo(
        (): boolean =>
            householdMembers
                .some((hhm: HouseholdMember): boolean => !!hhm.specialCircumstances.deceased),
        [ householdMembers ]
    );

    interface GetValuesConfig {
        applyingForCoverageIndicator: boolean | null,
        dateOfBirth: string | null,
        dateOfDeathValue: string | ReactElement,
        relationType: string
    }

    const getValues = (
        cellType: CellTypes,
        {
            applyingForCoverageIndicator,
            dateOfBirth,
            dateOfDeathValue,
            relationType
        } = { } as GetValuesConfig
    ): Array<any> => {

        const defaultLabelCells: Array<string> = [
            'ssap.input.option.relation',
            'ssap.input.label.birth_date',
            'common.seeking_coverage'
        ];

        switch(cellType) {
            case CellTypes.LABEL:
                return areDeceasedOnApplication
                    ? [
                        ...defaultLabelCells,
                        'ssap.input.label.death_date',
                        ...(additionalElement ? [ 'common.actions' ] : [])
                    ]
                    : [
                        ...defaultLabelCells,
                        ...(additionalElement ? [ 'common.actions' ] : [])
                    ];

            case CellTypes.VALUE:
            {
                const defaultValueCells: Array<string | boolean | null> = [
                    `ssap.input.option.${ relationType }`,
                    toUTCAndFormat(dateOfBirth || dayjsUtil(), MM_SLASH_DD_SLASH_YYYY),
                    applyingForCoverageIndicator
                ];

                return areDeceasedOnApplication
                    ? [
                        ...defaultValueCells,
                        dateOfDeathValue
                    ]
                    : defaultValueCells;
            }
        }
    };


    const getClassNames = () => {
        switch(true) {
            case !!(areDeceasedOnApplication && additionalElement):
                return { gridForSixColsCustomWidth: true }; // Deceased + Edit btn // Section summary
            case (areDeceasedOnApplication && !additionalElement):
                return { gridForSixColsCustomNoBtnWidth: true }; // Deceased + No Edit btn // Final summary
            case !!(!areDeceasedOnApplication && additionalElement):
                return { gridForFourClasses: true }; // No Deceased + Edit btn // Section summary
            case (!areDeceasedOnApplication && !additionalElement):
                return { gridForFourNoBtnClasses: true }; // No Deceased + No Edit btn // Final summary
        }
    };

    return (
        <div
            className = 'grid-row summary__block margin-b-20 gi-table__responsive'
            id = 'householdMemberSummary'
        >
            <table className = 'usa-table usa-table--borderless gi-table gi-table__borderless' >
                <thead>
                    <SummaryItem
                        { ...{ ...getClassNames() } }
                        renderAs = 'th'
                        textCode = 'common.name'
                        value = { getValues(CellTypes.LABEL) }
                        isDiffSwitchingIgnored
                        actionHeaderClassName="sr-only"
                    />
                </thead>

                <tbody>
                    {
                        householdMembers.map((member: HouseholdMember, i: number): ReactElement | null => {
                            const {
                                applicantGuid,
                                applyingForCoverageIndicator,
                                dateOfBirth,
                                name,
                                specialCircumstances: {
                                    deceasedDate
                                }
                            } = member;

                            const getRelationType = (hhms, bloodRelationship, applicantGuid) => {

                                const primaryContactGuid: string = householdMemberTools
                                    .getPrimaryContact(hhms)
                                    .applicantGuid;

                                const relationToHeadOfHousehold: BloodRelationship = bloodRelationship
                                    .find(rel =>
                                        rel.relatedPersonGuid === primaryContactGuid &&
                                        rel.personGuid === applicantGuid
                                    );


                                const resolvedRelations = loadedRelationsWholeList && loadedRelationsWholeList.length > 0
                                    ? loadedRelationsWholeList
                                    : loadedRelations;

                                const res: { type: string } | undefined = resolvedRelations
                                    .find(lr =>
                                        lr.code === (relationToHeadOfHousehold && relationToHeadOfHousehold.relation)
                                    );

                                return (res ? res.type : '').toLowerCase();
                            };


                            const getDateOfDeathValue = date => {
                                return date
                                    ? toUTCAndFormat(date, MM_SLASH_DD_SLASH_YYYY)
                                    : intlApi.formatMessage({ id: 'common.not_applicable' });

                            };

                            const fireGetValues = (...args) => {
                                const [
                                    applyingForCoverageIndicator,
                                    dateOfBirth,
                                    deceasedDate,
                                    applicantGuid,
                                    data
                                ] = args;

                                return getValues(
                                    CellTypes.VALUE,
                                    {
                                        applyingForCoverageIndicator,
                                        dateOfBirth,
                                        dateOfDeathValue: getDateOfDeathValue(deceasedDate),
                                        relationType: getRelationType(
                                            data.taxHousehold.householdMember,
                                            data.taxHousehold.bloodRelationship,
                                            applicantGuid
                                        )
                                    }
                                );
                            };

                            return (
                                <SummaryItem
                                    additionalElement = {
                                        additionalElement
                                            ? React.cloneElement(
                                                additionalElement,
                                                {
                                                    args: [
                                                        'editMember',
                                                        i,
                                                        `household-member-${ i }`
                                                    ],
                                                    ariaLabel: `Edit ${ name.firstName } ${ name.lastName }`
                                                }
                                            )
                                            : null
                                    }
                                    additionalElementClassName = { 'grid-col-auto text-center summary-button' }
                                    category = { normalizeNameOfPerson(name) }
                                    { ...{ ...getClassNames() } }
                                    ignoreIntl = { [ 1, 3 ] }
                                    key = { `${ name.firstName }_${ i }`}
                                    textCode = ''
                                    value = {
                                        getValues(
                                            CellTypes.VALUE,
                                            {
                                                applyingForCoverageIndicator,
                                                dateOfBirth,
                                                dateOfDeathValue: getDateOfDeathValue(deceasedDate),
                                                relationType: getRelationType(householdMembers, bloodRelationship, applicantGuid)
                                            }
                                        )
                                    }
                                    desc = {
                                        [
                                            [
                                                [
                                                    `taxHousehold.householdMember[${ i }].applyingForCoverageIndicator`,
                                                    `taxHousehold.householdMember[${ i }].dateOfBirth`,
                                                    `taxHousehold.householdMember[${ i }].specialCircumstances.deceasedDate`,
                                                    `taxHousehold.householdMember[${ i }].applicantGuid`,
                                                    'DATA'
                                                ],
                                                fireGetValues
                                            ]
                                        ]
                                    }
                                />
                            );
                        })
                    }
                </tbody>
            </table>
        </div>
    );
};

export default ApplyingForHealthCoverageSummary;
